import type { Job } from '../interfaces/job.interface'
import type { ImageAsset, OutputAsset } from '../interfaces'
import type { NordyUser } from '../interfaces/user.interface'

import { t } from '@/i18n'

import { JobStatus } from '../interfaces/job.interface'
import { nordyAxios } from './client'

export const jobApi = {
  createJobApi: async (dto: {
    prompt: string
    clientId: string
    workflow: string | undefined
    metadataEmbed?: boolean
    nordyHostname?: string
    nordyTargetId?: string
  }) => {
    const { data } = await nordyAxios.post<Job>('/v1/job', dto)
    return data
  },

  getJobById: async (jobId: string) => {
    const { data } = await nordyAxios.get<Job>(`/v1/job/${jobId}/status`)
    return data
  },

  getQueuePositionByJobId: async (jobId: string, count: number | null) => {
    const { data } = await nordyAxios.get<
      Job & {
        queuePosition: number
      }
    >(`/v1/job/${jobId}/queue-position?count=${count}`)
    return data
  },

  waitForJobPrompted: async (
    jobId: string,
    options?: { isCancelled?: () => boolean }
  ) => {
    const pollInterval = 2000
    const { useNordyQueueStore } = await import('../store/nordyQueueStore')
    const inProgressStatuses = new Set([
      JobStatus.Pending,
      JobStatus.Connecting,
      JobStatus.Connected,
      JobStatus.Running
    ])

    while (true) {
      if (options?.isCancelled?.()) {
        throw new Error(`waitForJobPrompted cancelled for jobId: ${jobId}`)
      }

      const job = await jobApi.getQueuePositionByJobId(
        jobId,
        useNordyQueueStore().queuePosition
      )
      await useNordyQueueStore().updateQueuePosition(job.queuePosition)

      if (options?.isCancelled?.()) {
        throw new Error(`waitForJobPrompted cancelled for jobId: ${jobId}`)
      }
      if (job?.promptResult) {
        return job
      }

      const currentJob = await jobApi.getJobById(jobId)
      if (options?.isCancelled?.()) {
        throw new Error(`waitForJobPrompted cancelled for jobId: ${jobId}`)
      }
      if (currentJob.promptResult) {
        return currentJob
      }
      if (!inProgressStatuses.has(currentJob.status)) {
        throw new Error(
          t('errorDialog.jobEndedBeforePrompt', { status: currentJob.status })
        )
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval))
    }
  },

  cancelJob: async (jobId: string) => {
    const { data } = await nordyAxios.patch<Job>(`/v1/job/${jobId}/cancel`)
    return data
  },

  getComfyJobHistory: async ({
    page = 1,
    limit = 30
  }: {
    page?: number
    limit?: number
  } = {}) => {
    const { data } = await nordyAxios.get<
      Array<
        Job & {
          _id: string
          outputAsset?: OutputAsset<NordyUser, string, ImageAsset>[]
        }
      >
    >(`/v1/job/comfy/history?page=${page}&limit=${limit}`)
    return data
  }
}
